import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import BookingHistory from "../components/BookingHistory";
import { useMilestone } from "../contexts/MilestoneContext";
import Progress from "./B2B/Progress";
import "./Navigation.css";
import ShortlistStatic from "./ShortlistStatic";

const Navigation = () => {
  const isAuthenticated = useSelector(
    (state: any) => state.auth.isAuthenticated
  );
  const userFullName =
    useSelector((state: any) => state.auth.userFullName) ?? "";
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  const clientName = useSelector((state: any) => state.auth.clientName) ?? "";

  // Generate user initials from full name
  const getUserInitials = (fullName: string): string => {
    if (!fullName || fullName.trim() === "") return "U";

    const names = fullName
      .trim()
      .split(" ")
      .filter((name) => name.length > 0);
    if (names.length === 0) return "U";
    if (names.length === 1) return names[0].charAt(0).toUpperCase();

    // Take first letter of first name and first letter of last name
    return (
      names[0].charAt(0) + names[names.length - 1].charAt(0)
    ).toUpperCase();
  };

  const userInitials = getUserInitials(userFullName);

  const dispatch = useDispatch();
  const [navbarOpen, setNavbarOpen] = useState(false);
  const [showShortlist, setShowShortlist] = useState(false);
  const [showBookingHistory, setShowBookingHistory] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setCurrentStep } = useMilestone();

  // Handle shortlist step changes
  const handleShortlistStepChange = (step: number) => {
    setCurrentStep(step);
  };

  // Handle closing shortlist modal
  const handleShortlistClose = () => {
    setShowShortlist(false);
    // Reset to step 1 when modal is closed
    setCurrentStep(1);
  };

  useEffect(() => {
    // Close navbar on route change
    setNavbarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    console.log(
      "Navigation component mounted",
      isAuthenticated,
      userFullName,
      userRole
    );
  }, [isAuthenticated, userFullName, userRole]);

  useEffect(() => {
    const body = document.body;
    if (userRole === "B2BUser" && isAuthenticated) {
      body.classList.add("b2b-user-authenticated");
    } else {
      body.classList.remove("b2b-user-authenticated");
    }

    return () => {
      body.classList.remove("b2b-user-authenticated");
    };
  }, [userRole, isAuthenticated]);

  const handleLogout = () => {
    dispatch({ type: "auth/logout" });
    navigate("/login"); // navigate to login after logout
    // Assumes you have an auth slice with a logout reducer
  };

  return (
    <>
      <header className="bg-light border-bottom fixed-top ">
        <div className="container-fluid">
          <nav
            className="navbar navbar-expand-lg navbar-light"
            data-aos-duration="3000"
          >
            <Link
              className="navbar-brand"
              to={
                userRole === "B2BUser"
                  ? "/b2b-property"
                  : userRole === "Administrator"
                  ? "/admin/dashboard"
                  : userRole === "RMCAdmin"
                  ? "/rmc/dashboard"
                  : "/home"
              }
            >
              <img
                src="/Images/logo.png"
                alt="Logo"
                className="header-logo"
                data-aos="fade-right"
              />
            </Link>
            <button
              className="navbar-toggler"
              type="button"
              aria-controls="navbarContent"
              aria-expanded={navbarOpen}
              aria-label="Toggle navigation"
              onClick={() => setNavbarOpen(!navbarOpen)}
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            <div
              className={`collapse navbar-collapse${navbarOpen ? " show" : ""}`}
              id="navbarContent"
              data-aos="fade-up"
            >
              {/* Navigation links - next to logo */}
              <ul className="navbar-nav mb-2 mb-lg-0 nav-links">
                <li className="nav-item">
                  <Link
                    className="nav-link"
                    to={
                      userRole === "B2BUser"
                        ? "/b2b-property"
                        : userRole === "Administrator"
                        ? "/admin/dashboard"
                        : userRole === "RMCAdmin"
                        ? "/rmc/dashboard"
                        : "/home"
                    }
                  >
                    Home
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/about-us">
                    About Us
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/contact-us">
                    Contact Us
                  </Link>
                </li>
              </ul>

              {/* Progress Stepper - Only for B2BUser */}
              {userRole === "B2BUser" && (
                <div className="d-flex align-items-center flex-grow-1 mx-3">
                  <Progress />
                </div>
              )}

              {/* Right: Shortlist Button and User Actions */}
              <div
                className="d-flex align-items-center ms-auto"
                data-aos="fade-left"
              >
                {isAuthenticated ? (
                  <>
                    {/* Shortlist Button - Only visible for B2BUser role */}
                    {userRole === "B2BUser" && (
                      <button
                        onClick={() => setShowShortlist(true)}
                        className="btn btn-outline-danger me-2 d-flex align-items-center shortlist-btn"
                        title="Shortlisted Rooms"
                        data-tooltip="Shortlisted Rooms"
                      >
                        <Icon.Heart size={18} />
                      </button>
                    )}

                    {/* Booking History Button */}
                    {userRole === "B2BUser" && (
                      <button
                        onClick={() => setShowBookingHistory(true)}
                        className="btn btn-outline-primary me-2 d-flex align-items-center history-btn"
                        title="Booking History"
                        data-tooltip="Booking History"
                      >
                        <Icon.ClockHistory size={18} />
                      </button>
                    )}

                    <Dropdown className="user-actions">
                      <Dropdown.Toggle
                        className="dropdown-profile d-flex align-items-center"
                        id="dropdown-basic"
                      >
                        <div className="avatar avatar-sm mb-15 me-2">
                          <div className="d-flex align-items-center justify-content-center">
                            {userInitials}
                          </div>
                        </div>
                        <span className="user-name text-dark">
                          {userFullName || "User"}
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        align={"end"}
                        className="dropdown-menu-right menu-actions"
                      >
                        <Dropdown.Header>
                          <div className="media align-items-center">
                            <div className="avatar avatar-sm mb-15">
                              <div className="d-flex align-items-center justify-content-center">
                                {userInitials}
                              </div>
                            </div>
                            <div className="media-body pl-15">
                              <h6 className="_600">
                                {userFullName || <>&nbsp;</>}
                              </h6>
                              {clientName && (
                                <span className="lead tx-12 _400">
                                  {clientName}
                                </span>
                              )}
                            </div>
                          </div>
                        </Dropdown.Header>
                        <Dropdown.Divider />
                        <Dropdown.Item as={NavLink} to="/my-profile">
                          <Icon.Person className="me-2" /> Profile
                        </Dropdown.Item>
                        <Dropdown.Item as="button" onClick={handleLogout}>
                          <Icon.Power className="me-2" /> Logout
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
          </nav>
        </div>
      </header>

      <ShortlistStatic
        show={showShortlist}
        onHide={handleShortlistClose}
        onStepChange={handleShortlistStepChange}
      />

      <BookingHistory
        show={showBookingHistory}
        onHide={() => setShowBookingHistory(false)}
      />
    </>
  );
};

export default Navigation;
